import React, { ReactText, useCallback, useEffect } from "react";
import { inject, observer } from "mobx-react";
import { ADD_ROW, EXPORT_TO_EXCEL } from "constants/translates";
import { DateRangeDate, IObjectMap } from "interfaces/common";
import {
  LOGISTIC_ACTIVE_COLUMNS,
  LOGISTIC_ORDER_COLUMNS,
} from "constants/settings";
import LogisticStore from "stores/LogisticStore";
import { Form } from "antd";
import ActionButton from "../components/declarants/groupButton";
import TableWrapper from "../components/declarants/tableWrapper";
import Table from "../components/common/Table";
import DateRange from "../components/declarants/dateRange";
import Search from "../components/declarants/search";
import Settings from "../components/declarants/Settings";
import COLUMNS from "../components/tables/logisticTable/config";
import updateSortingAndFiltersOfColumns from "../helpers/filters";
import { ColumnsType } from "antd/lib/table/interface";
import SettingsStore from "stores/SettingsStore";
import UserStore from "stores/UserStore";
import CollapseHeader from "components/CollapseHeader";
import Card from "components/Card/Card";
import ee from "services/EventEmitter";
import { ADD_NEW_ROW } from "constants/events";
import { Logistic } from "models/Logistic";

let isLoaded = false;

const temporaryLinesCheck = (data: Logistic[]) => {
  let withTemporary = false;

  data?.forEach((row) => {
    if (row?.temporary) {
      withTemporary = true;
    }

    if (row?.children && row?.children.length > 0) {
      row?.children.forEach((child) => {
        if (child?.temporary) {
          withTemporary = true;
        }
      });
    }
  });

  return withTemporary;
};

const LogisticPage: React.FC<{
  settingsStore: SettingsStore;
  userStore: UserStore;
}> = ({ settingsStore, userStore }) => {
  const [form] = Form.useForm();

  const withTemporary = temporaryLinesCheck(LogisticStore.data as Logistic[]);
  const addRowActivity = LogisticStore.selectedRows.length < 2;
  const disabledAddButton = !addRowActivity || withTemporary;
  const clients = settingsStore.clients;
  const downloadIsDisabled = !(LogisticStore?.data?.length > 0);

  useEffect(() => {
    if (isLoaded) return;

    LogisticStore.load({}, true);
    isLoaded = true;
  }, []);

  const onLoadHandler = useCallback(
    (parameters?: IObjectMap, forceUpdate?: boolean): void => {
      LogisticStore.load(
        {
          ...parameters,
          searchPhrase: LogisticStore.parameters.searchPhrase,
          start_date: LogisticStore.parameters.start_date,
          end_date: LogisticStore.parameters.end_date,
        },
        forceUpdate
      );
    },
    []
  );

  const handleAdd = () => {
    ee.emit(ADD_NEW_ROW, "");
  };

  useEffect(() => {
    settingsStore?.setColumns(
      updateSortingAndFiltersOfColumns(
        userStore.authorizedOnly(COLUMNS(form, clients, userStore)) ||
          COLUMNS(form, clients, userStore),
        LogisticStore.parameters
      ),
      LOGISTIC_ORDER_COLUMNS,
      LOGISTIC_ACTIVE_COLUMNS
    );
  }, [
    form,
    settingsStore,
    settingsStore.settings,
    userStore,
    userStore.user,
    settingsStore.isEnabledReorder,
    LogisticStore.parameters,
    LogisticStore.editingKeys,
    LogisticStore.tcOptions,
    LogisticStore.contractorOptions,
    clients,
  ]);

  const getCheckboxProps = (record: any) => {
    const isVisible = record?.children
      ? { display: "block" }
      : { display: "none" };

    return {
      style: isVisible,
    };
  };

  const downloadLogisticsAsExcelFile = () => {
    const activeColumnKey = settingsStore.columns
      .filter((column) => column.isActive && column.key !== "actions")
      .map((column) => {
        return { [column.key]: column.title };
      });

    LogisticStore.downloadExcel({
      activeColumn: activeColumnKey,
      ...LogisticStore.parameters,
    });
  };

  return (
    <>
      <CollapseHeader>
        <DateRange
          store={LogisticStore}
          startDate={LogisticStore.parameters.start_date as DateRangeDate}
          endDate={LogisticStore.parameters.end_date as DateRangeDate}
          allowClear={true}
        />
        <Search store={LogisticStore} />
        <div />
      </CollapseHeader>
      <Card>
        <Card.Header>
          <ActionButton
            onClick={handleAdd}
            text={ADD_ROW}
            disabled={disabledAddButton}
          />
          <ActionButton
            onClick={downloadLogisticsAsExcelFile}
            disabled={downloadIsDisabled || LogisticStore.isLoadingButton}
            text={EXPORT_TO_EXCEL}
            loading={LogisticStore.isLoadingButton}
          />
          <Settings reorderable />
        </Card.Header>
        <TableWrapper
          enableScrollBar={settingsStore.isEnabledScrollbar}
          className="logistic-table"
        >
          <Form form={form} component={false}>
            <Table
              key="loading-done"
              columns={settingsStore.activeColumns as ColumnsType<Logistic>}
              settingsStore={settingsStore}
              data={LogisticStore.data}
              onLoad={onLoadHandler}
              hasMoreItems={LogisticStore.hasMoreItems}
              isLoading={LogisticStore.isLoadingTableData}
              onRowSelectors={(items: ReactText[]) => {
                LogisticStore.changeSelectedRows(items);
              }}
              rowClassName={(record) => {
                if ((record as Logistic).is_paid) {
                  return "logistic-paid";
                }

                if ((record as Logistic).is_ready) {
                  return "logistic-ready";
                }

                return "";
              }}
              selectedRows={LogisticStore.selectedRows}
              checkStrictly={true}
              expandableConfig={{
                expandRowByClick: false,
                indentSize: 10,
              }}
              onExpandedRowsChange={(expandedRows) => {
                LogisticStore.expandedRows = expandedRows;
              }}
              expandedRows={LogisticStore.expandedRows}
              getCheckboxProps={getCheckboxProps}
              hideSelectAll
            />
          </Form>
        </TableWrapper>
      </Card>
    </>
  );
};

export default inject(({ store }) => ({
  settingsStore: store.settingsStore,
  userStore: store.userStore,
}))(observer(LogisticPage));
